import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

import { PreviousThreads } from '../../../components/previous-threads/previous-threads';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { ThreadMessage } from '@/types';

interface AnswerSidebarProps {
  isLoadingMessages: boolean;
  userMessage: ThreadMessage | undefined;
}

const ChatBubble = ({ text, isLoadingMessages }: { text: string; isLoadingMessages: boolean }) => {
  return (
    <div className="max-h-52 w-fit max-w-[90%] overflow-y-auto rounded-xl rounded-bl-none border bg-gray-50 px-3 py-2 text-xs whitespace-pre-wrap">
      {isLoadingMessages ? <Skeleton className="h-4 w-24" /> : text}
    </div>
  );
};

export function AnswerSidebar({ userMessage, isLoadingMessages }: AnswerSidebarProps) {
  const { matterId, subsetId } = useParams();
  const navigate = useNavigate();
  return (
    <div className="flex h-full w-56 min-w-56 flex-col border-r">
      <motion.div
        initial={{ height: '50%' }}
        animate={{ height: '50%' }}
        className="flex flex-col border-b border-gray-100"
      >
        <div className="flex h-14 items-center justify-between border-b border-gray-100 bg-gray-50/50 px-4 py-3">
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button asChild size="iconSm" className="rounded-full p-1">
                  <Link to={`/matters/${matterId}/subsets/${subsetId}/kim`}>
                    <Icon className="lucide--arrow-left" />
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Back to all questions</TooltipContent>
            </Tooltip>

            <h3 className="text-sm font-medium text-gray-800">Question</h3>
          </div>

          <Button
            onClick={() => {
              navigate(`/matters/${matterId}/subsets/${subsetId}/kim`);
            }}
            size="sm"
            variant="primary"
            disabled={isLoadingMessages}
          >
            <Icon className="lucide--plus" />
            New
          </Button>
        </div>
        <div className="flex flex-1 flex-col items-start gap-2 overflow-hidden p-4">
          <ChatBubble text={userMessage ? userMessage.content : '...'} isLoadingMessages={isLoadingMessages} />
        </div>
      </motion.div>

      <motion.div initial={{ height: '50%' }} animate={{ height: '50%' }} className="flex flex-col">
        <div className="h-12 border-b border-gray-100 bg-gray-50/50 px-4 py-3">
          <h3 className="text-sm font-medium text-gray-800">Question History</h3>
        </div>
        <div className="flex-1 overflow-auto">
          <PreviousThreads />
        </div>
      </motion.div>
    </div>
  );
}
